import React, { useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Box, Typography, Alert, Tooltip,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';
import ClassificationBadge from './ClassificationBadge';
import { useSnackbar } from '../contexts/SnackbarContext';
import useDmsPalette from '../hooks/useDmsPalette';

export default function ShareDialog({ open, onClose, document }) {
  const MP = useDmsPalette();
  const { showSnack } = useSnackbar();
  const [expiryDate, setExpiryDate] = useState('');

  if (!document) return null;

  const shareLink = `${window.location.origin}/shared/${document._id}`;

  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      showSnack('Link copied!', 'success');
    } catch {
      showSnack('Failed to copy link', 'error');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ShareIcon sx={{ color: MP.blue }} />
        Share Document
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        {/* Document header */}
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 1, mb: 2,
          p: 1.5, bgcolor: MP.deepBlue, borderRadius: 1,
          border: `1px solid ${MP.border}`,
        }}>
          <Typography variant="subtitle2" fontWeight={600} sx={{ flex: 1 }} noWrap>
            {document.title}
          </Typography>
          <ClassificationBadge classification={document.classification} />
        </Box>

        {/* Share link */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Shareable Link
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
          <TextField
            fullWidth size="small"
            value={shareLink}
            InputProps={{ readOnly: true }}
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: MP.surface,
                fontFamily: 'monospace',
                fontSize: '0.8rem',
              },
            }}
          />
          <Tooltip title="Copy to clipboard">
            <Button
              variant="contained"
              onClick={handleCopyLink}
              startIcon={<ContentCopyIcon />}
              size="small"
              sx={{ whiteSpace: 'nowrap' }}
            >
              Copy
            </Button>
          </Tooltip>
        </Box>

        {/* Expiry date (optional) */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Link Expiry <Typography component="span" variant="caption" color="text.secondary">(optional)</Typography>
        </Typography>
        <TextField
          type="date"
          size="small"
          fullWidth
          value={expiryDate}
          onChange={(e) => setExpiryDate(e.target.value)}
          inputProps={{ min: new Date().toISOString().split('T')[0] }}
          sx={{ mb: 2 }}
        />

        {/* Email invite */}
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Email Invite
        </Typography>
        <TextField
          fullWidth size="small"
          placeholder="Enter email addresses, comma-separated…"
          disabled
          sx={{ mb: 1.5 }}
        />
        <Alert severity="info">
          This feature requires enterprise subscription.
        </Alert>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
